import { EditOutlined, TrophyOutlined } from "@ant-design/icons"
import type { TableProps } from "antd"
import { Table, Tag, Typography, Button, Tooltip, Image, Space } from "antd"
import type React from "react"
import type { LevelItem } from "@/services/gamification/levelApi"
import { useTableScroll } from "@/hooks/useTableScroll"
import "@/styles/table.full.css"
import { formatDateTime } from "@/utils/dateUtils"

const { Text } = Typography

interface LevelTableProps {
  data: LevelItem[]
  loading: boolean
  onEdit: (record: LevelItem) => void
}

const LevelTable: React.FC<LevelTableProps> = ({ data, loading, onEdit }) => {
  const { scrollConfig, tableWrapperRef } = useTableScroll()

  const columns: TableProps<LevelItem>["columns"] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 70,
      align: "center",
      render: (value: number) => <Tag color="blue">#{value}</Tag>,
    },
    {
      title: "Biểu tượng",
      key: "image",
      width: 100,
      align: "center",
      render: (_: any, record: LevelItem) => {
        if (record.image_url) {
          return (
            <div className="flex justify-center">
              <Image
                src={record.image_url}
                alt={record.name}
                width={44}
                height={44}
                className="rounded-lg border border-gray-100 bg-gray-50 object-contain p-1"
              />
            </div>
          )
        }
        return (
          <div className="flex items-center justify-center">
            <div className="flex h-11 w-11 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-amber-500">
              <TrophyOutlined className="text-xl" />
            </div>
          </div>
        )
      },
    },
    {
      title: "Tên cấp độ",
      dataIndex: "name",
      key: "name",
      width: 220,
      render: (value: string, record: LevelItem) => (
        <div className="flex flex-col">
          <span className="font-semibold text-gray-900">{value}</span>
          <span className="text-gray-400 text-xs">Level #{record.id}</span>
        </div>
      ),
    },
    {
      title: "Điểm yêu cầu",
      dataIndex: "min_point",
      key: "min_point",
      width: 150,
      render: (value: number) => (
        <Tag color="gold" className="rounded-full px-2.5 py-0.5 font-semibold">
          {value.toLocaleString("vi-VN")} pts
        </Tag>
      ),
    },
    {
      title: "Mô tả",
      dataIndex: "description",
      key: "description",
      render: (value?: string | null) => (
        <span className="text-gray-600">{value || <Text type="secondary">—</Text>}</span>
      ),
    },
    {
      title: "Thao tác",
      key: "actions",
      fixed: "right",
      width: 90,
      align: "center",
      render: (_: any, record: LevelItem) => (
        <Space size="small">
          <Tooltip title="Chỉnh sửa cấp độ">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined className="text-base text-orange-500" />}
              onClick={() => onEdit(record)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <Table
      bordered
      columns={columns}
      dataSource={data}
      rowKey="id"
      loading={loading}
      pagination={false}
      scroll={{ x: "100%", y: scrollConfig.y }}
      size="middle"
      classNames={{
        root: "full-height-table",
      }}
    />
  )
}

LevelTable.displayName = "LevelTable"

export default LevelTable
